import type {
  HistoryResponse,
  LeagueBundle,
  ScoresResponse,
  TeamRecord,
} from '../../shared/types';

/**
 * The league record book.
 *
 * Everything here is keyed on **user_id**, never roster_id. Sleeper mints a new
 * league each season with a fresh set of roster ids, and they happen to line up
 * for this league today only because the same ten managers carried over. A
 * roster-keyed record book would look perfectly correct until the first year
 * they shift, then silently attribute one manager's streak to another.
 */

/** One side of one game. Two of these exist per game played. */
export interface Appearance {
  season: string;
  week: number;
  isPlayoff: boolean;
  userId: string;
  opponentId: string;
  /**
   * Roster ids for this game, in the numbering of the season it was played in.
   *
   * Nothing about a manager's identity should ever be read off these -- Sleeper
   * renumbers rosters every August, which is the whole reason this file keys on
   * `userId`. They are carried for one purpose: a season's playoff bracket
   * names its matches by roster pair, so classifying a playoff-week game as a
   * real bracket game or a consolation one needs the pair to join on.
   */
  rosterId: number;
  opponentRosterId: number;
  points: number;
  against: number;
  /** Positive when this side won. */
  margin: number;
  result: 'W' | 'L' | 'T';
}

/** One game, counted once — for records about the game rather than a side. */
export interface Meeting {
  season: string;
  week: number;
  isPlayoff: boolean;
  /** The higher-scoring side (either one, if the game was tied). */
  winner: Appearance;
  loser: Appearance;
  combined: number;
  /** Always non-negative. */
  margin: number;
}

export interface History {
  appearances: Appearance[];
  meetings: Meeting[];
}

/**
 * Flattens every season's games into a single chronological list.
 *
 * The sort matters more than it looks: `useScores` returns one response per
 * season in the order the league ids were passed, which is newest-first. Walking
 * that order to find a streak computes it backwards and runs across the season
 * boundary in the wrong direction, producing a number that is wrong but
 * entirely plausible.
 */
export function flattenHistory(seasons: ScoresResponse[], includePlayoffs: boolean): History {
  const appearances: Appearance[] = [];
  const meetings: Meeting[] = [];

  for (const season of seasons) {
    for (const g of season.games) {
      if (!includePlayoffs && g.isPlayoff) continue;
      const userA = season.rosterOwners[String(g.rosterA)];
      const userB = season.rosterOwners[String(g.rosterB)];
      // An abandoned roster can have a null owner; it belongs to no manager.
      if (!userA || !userB || userA === userB) continue;

      const side = (
        userId: string,
        opponentId: string,
        rosterId: number,
        opponentRosterId: number,
        points: number,
        against: number,
      ): Appearance => ({
        season: season.season,
        week: g.week,
        isPlayoff: g.isPlayoff,
        userId,
        opponentId,
        rosterId,
        opponentRosterId,
        points,
        against,
        margin: Math.round((points - against) * 100) / 100,
        // Ties are rare but possible; `Game` carries no tie flag, so equality
        // is the only signal and assuming a winner exists would be wrong.
        result: points > against ? 'W' : points < against ? 'L' : 'T',
      });

      const a = side(userA, userB, g.rosterA, g.rosterB, g.pointsA, g.pointsB);
      const b = side(userB, userA, g.rosterB, g.rosterA, g.pointsB, g.pointsA);
      appearances.push(a, b);

      const [winner, loser] = a.points >= b.points ? [a, b] : [b, a];
      meetings.push({
        season: season.season,
        week: g.week,
        isPlayoff: g.isPlayoff,
        winner,
        loser,
        combined: Math.round((a.points + b.points) * 100) / 100,
        margin: Math.abs(a.margin),
      });
    }
  }

  const chrono = (x: { season: string; week: number }, y: { season: string; week: number }) =>
    Number(x.season) - Number(y.season) || x.week - y.week;

  appearances.sort(chrono);
  meetings.sort(chrono);
  return { appearances, meetings };
}

// --- Boards ----------------------------------------------------------------

export type ValueKind = 'points' | 'count' | 'pct' | 'record';

export interface RecordRow {
  userId: string;
  /** The number the board ranks on. */
  value: number;
  /** Pre-rendered where the value alone does not tell the story. */
  detail?: string;
  opponentId?: string;
  season: string;
  /** Null for season-long and streak records that span weeks. */
  week: number | null;
  isPlayoff: boolean;
}

export interface RecordBoard {
  key: string;
  title: string;
  hint: string;
  kind: ValueKind;
  /** Best first, capped at TOP. */
  rows: RecordRow[];
}

const TOP = 5;

function board(
  key: string,
  title: string,
  hint: string,
  kind: ValueKind,
  rows: RecordRow[],
): RecordBoard {
  return { key, title, hint, kind, rows: rows.slice(0, TOP) };
}

const pts = (n: number) => n.toFixed(2);

function fromAppearance(a: Appearance, value: number, detail?: string): RecordRow {
  return {
    userId: a.userId,
    value,
    detail,
    opponentId: a.opponentId,
    season: a.season,
    week: a.week,
    isPlayoff: a.isPlayoff,
  };
}

export function singleGameBoards({ appearances, meetings }: History): RecordBoard[] {
  const byPoints = (dir: 1 | -1) => (x: Appearance, y: Appearance) => dir * (y.points - x.points);

  return [
    board(
      'high',
      'Highest Score',
      'Most points, one team, one week',
      'points',
      [...appearances]
        .sort(byPoints(1))
        .map((a) => fromAppearance(a, a.points, `beat ${pts(a.against)}`)),
    ),
    board(
      'low',
      'Lowest Score',
      'Fewest points, one team, one week',
      'points',
      [...appearances]
        .sort(byPoints(-1))
        .map((a) => fromAppearance(a, a.points, `against ${pts(a.against)}`)),
    ),
    board(
      'blowout',
      'Biggest Blowout',
      'Largest margin of victory',
      'points',
      [...appearances]
        .filter((a) => a.result === 'W')
        .sort((x, y) => y.margin - x.margin)
        .map((a) => fromAppearance(a, a.margin, `${pts(a.points)} – ${pts(a.against)}`)),
    ),
    board(
      'close',
      'Closest Game',
      'Smallest margin of victory',
      'points',
      meetings
        .slice()
        .sort((x, y) => x.margin - y.margin)
        .map((m) => ({
          userId: m.winner.userId,
          value: m.margin,
          detail: `${pts(m.winner.points)} – ${pts(m.loser.points)}`,
          opponentId: m.loser.userId,
          season: m.season,
          week: m.week,
          isPlayoff: m.isPlayoff,
        })),
    ),
    board(
      'unlucky',
      'Most Points In A Loss',
      'Highest losing score',
      'points',
      [...appearances]
        .filter((a) => a.result === 'L')
        .sort(byPoints(1))
        .map((a) => fromAppearance(a, a.points, `lost to ${pts(a.against)}`)),
    ),
    board(
      'lucky',
      'Fewest Points In A Win',
      'Lowest winning score',
      'points',
      [...appearances]
        .filter((a) => a.result === 'W')
        .sort(byPoints(-1))
        .map((a) => fromAppearance(a, a.points, `beat ${pts(a.against)}`)),
    ),
    board(
      'combined',
      'Highest Combined Total',
      'Both scores added',
      'points',
      meetings
        .slice()
        .sort((x, y) => y.combined - x.combined)
        .map((m) => ({
          userId: m.winner.userId,
          value: m.combined,
          detail: `${pts(m.winner.points)} – ${pts(m.loser.points)}`,
          opponentId: m.loser.userId,
          season: m.season,
          week: m.week,
          isPlayoff: m.isPlayoff,
        })),
    ),
  ];
}

// --- Season totals ---------------------------------------------------------

export interface SeasonTally {
  userId: string;
  season: string;
  wins: number;
  losses: number;
  ties: number;
  points: number;
  games: number;
}

export function seasonTallies(appearances: Appearance[]): SeasonTally[] {
  const map = new Map<string, SeasonTally>();
  for (const a of appearances) {
    const k = `${a.userId}|${a.season}`;
    const t = map.get(k) ?? {
      userId: a.userId,
      season: a.season,
      wins: 0,
      losses: 0,
      ties: 0,
      points: 0,
      games: 0,
    };
    t.games += 1;
    t.points = Math.round((t.points + a.points) * 100) / 100;
    if (a.result === 'W') t.wins += 1;
    else if (a.result === 'L') t.losses += 1;
    else t.ties += 1;
    map.set(k, t);
  }
  return [...map.values()];
}

export const tallyPct = (t: SeasonTally) => (t.games === 0 ? 0 : (t.wins + t.ties * 0.5) / t.games);
const tallyText = (t: SeasonTally) =>
  t.ties > 0 ? `${t.wins}-${t.losses}-${t.ties}` : `${t.wins}-${t.losses}`;

export function seasonBoards(appearances: Appearance[]): RecordBoard[] {
  const tallies = seasonTallies(appearances);
  const row = (t: SeasonTally, value: number, detail: string): RecordRow => ({
    userId: t.userId,
    value,
    detail,
    season: t.season,
    week: null,
    isPlayoff: false,
  });

  return [
    board(
      'season-points',
      'Most Points In A Season',
      'Points for, full season',
      'points',
      [...tallies]
        .sort((a, b) => b.points - a.points)
        .map((t) => row(t, t.points, `${tallyText(t)} over ${t.games} games`)),
    ),
    board(
      'best-record',
      'Best Season Record',
      'Regular season and playoffs',
      'record',
      [...tallies]
        // A two-game sample would otherwise top a 14-game season on win rate.
        .filter((t) => t.games >= 4)
        .sort((a, b) => tallyPct(b) - tallyPct(a) || b.wins - a.wins)
        .map((t) => row(t, tallyPct(t), tallyText(t))),
    ),
    board(
      'worst-record',
      'Worst Season Record',
      'Fewest wins, full season',
      'record',
      [...tallies]
        .filter((t) => t.games >= 4)
        .sort((a, b) => tallyPct(a) - tallyPct(b) || a.wins - b.wins)
        .map((t) => row(t, tallyPct(t), tallyText(t))),
    ),
  ];
}

// --- Streaks ---------------------------------------------------------------

export interface Streak {
  userId: string;
  kind: 'W' | 'L';
  length: number;
  from: { season: string; week: number };
  to: { season: string; week: number };
  /** True when the run reaches that manager's most recent game. */
  active: boolean;
}

/**
 * Every unbroken run of wins or losses, per manager, across all seasons.
 *
 * Expects `appearances` in chronological order — `flattenHistory` guarantees
 * that. A tie ends both a winning and a losing run rather than extending either.
 */
export function allStreaks(appearances: Appearance[]): Streak[] {
  const byUser = new Map<string, Appearance[]>();
  for (const a of appearances) {
    byUser.set(a.userId, [...(byUser.get(a.userId) ?? []), a]);
  }

  const out: Streak[] = [];
  for (const [userId, games] of byUser) {
    let run: Appearance[] = [];
    const flush = (endsHistory: boolean) => {
      if (run.length === 0) return;
      const kind = run[0].result;
      if (kind === 'T') {
        run = [];
        return;
      }
      out.push({
        userId,
        kind,
        length: run.length,
        from: { season: run[0].season, week: run[0].week },
        to: { season: run[run.length - 1].season, week: run[run.length - 1].week },
        active: endsHistory,
      });
      run = [];
    };

    for (const g of games) {
      if (run.length > 0 && run[0].result !== g.result) flush(false);
      if (g.result === 'T') {
        run = [];
        continue;
      }
      run.push(g);
    }
    flush(true);
  }

  return out;
}

/** Best run per manager, so one dominant season cannot fill the whole board.
 *
 *  Exported because a tie count has to be taken over the same set the board
 *  ranks -- counting raw streaks instead would report a manager whose second-best
 *  run happens to equal the leader as a co-holder. */
export function bestPerManager(streaks: Streak[]): Streak[] {
  const best = new Map<string, Streak>();
  for (const s of streaks) {
    const held = best.get(s.userId);
    if (!held || s.length > held.length) best.set(s.userId, s);
  }
  return [...best.values()].sort((a, b) => b.length - a.length);
}

function streakRow(s: Streak): RecordRow {
  // Every consumer prints `season` beside the row on its own -- the record
  // book's hero line, its runner-up list and the home page card all build it
  // from `season`/`week`. So a within-season span must NOT repeat the year, or
  // the card reads "2025" above "2025 weeks 1-6": the same headline-and-subtitle
  // duplication the record book already shipped once. A run that crosses
  // seasons keeps both years, because the earlier one is not this row's
  // `season` and cannot be inferred from it.
  const span =
    s.from.season !== s.to.season
      ? `${s.from.season} wk ${s.from.week} → ${s.to.season} wk ${s.to.week}`
      : s.from.week === s.to.week
        ? `week ${s.from.week}`
        : `weeks ${s.from.week}–${s.to.week}`;
  return {
    userId: s.userId,
    value: s.length,
    detail: s.active ? `${span} · active` : span,
    season: s.to.season,
    week: null,
    isPlayoff: false,
  };
}

export function streakBoards(appearances: Appearance[]): RecordBoard[] {
  const streaks = allStreaks(appearances);
  const active = streaks.filter((s) => s.active).sort((a, b) => b.length - a.length);

  return [
    board(
      'win-streak',
      'Longest Win Streak',
      'Consecutive wins, spanning seasons',
      'count',
      bestPerManager(streaks.filter((s) => s.kind === 'W')).map(streakRow),
    ),
    board(
      'lose-streak',
      'Longest Losing Streak',
      'Consecutive losses, spanning seasons',
      'count',
      bestPerManager(streaks.filter((s) => s.kind === 'L')).map(streakRow),
    ),
    board(
      'current-streak',
      'Current Streak',
      'Active run, wins or losses',
      'count',
      active.map((s) => {
        // "win"/"loss" do not share a plural suffix -- appending "es" to both
        // yields "5 wines".
        const noun = s.kind === 'W' ? (s.length === 1 ? 'win' : 'wins') : s.length === 1 ? 'loss' : 'losses';
        return {
          ...streakRow(s),
          detail: `${s.length} ${noun} · since ${s.from.season} wk ${s.from.week}`,
        };
      }),
    ),
  ];
}

// --- Lineup efficiency -----------------------------------------------------

/**
 * Seasons shorter than this are dropped, exactly as `best-record` drops them.
 * Efficiency is a RATIO, so a manager who happened to nail week 1 of a season
 * in progress would otherwise outrank every completed season until autumn.
 */
const MIN_EFFICIENCY_GAMES = 4;

/**
 * `fpts / ppts` -- the share of available points a manager actually started.
 *
 * This is the one record that cannot come from `/api/scores`: `Game` carries
 * only team totals. Potential points live on `TeamRecord`, which reaches the
 * client through the season placements in `/api/history` and the current
 * season's own roster list.
 *
 * **Both figures are REGULAR SEASON ONLY, and nothing in their names says so.**
 * Measured across all ten 2025 rosters: `fpts` equals the sum of that roster's
 * weeks 1-14 scores exactly, with the playoff weeks absent -- thetale reads
 * 1963.54 here against the 2401.70 that `season-points` reports for the same
 * manager and the same season. So this board and that one disagree by design,
 * and the disagreement is invisible because both numbers are plausible and
 * neither is labelled. Any caption on this board has to name the regular
 * season, and it matters more now that the two sit beside each other on the
 * home page.
 *
 * Returned uncapped, because a tie count has to be taken over every row rather
 * than over the five `board()` keeps.
 */
export function efficiencyRows(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
): RecordRow[] {
  const rows: RecordRow[] = [];

  const add = (userId: string | null, season: string, r: TeamRecord) => {
    const games = r.wins + r.losses + r.ties;
    if (!userId || r.pointsFor <= 0 || r.potentialPoints <= 0) return;
    if (games < MIN_EFFICIENCY_GAMES) return;
    rows.push({
      userId,
      value: r.pointsFor / r.potentialPoints,
      detail: `${pts(r.pointsFor)} of ${pts(r.potentialPoints)} possible`,
      season,
      week: null,
      isPlayoff: false,
    });
  };

  for (const s of history?.seasons ?? []) {
    // `placements` is empty until a season's bracket exists; `regularSeason` is
    // always populated and carries the same records.
    for (const p of s.regularSeason) add(p.owner_id, s.season, p.record);
  }
  for (const t of bundle.teams) add(t.owner_id, bundle.league.season, t.record);

  return rows.sort((a, b) => b.value - a.value);
}

export function efficiencyBoard(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
): RecordBoard {
  return board(
    'efficiency',
    'Best Lineup Efficiency',
    'Optimal lineup share, regular season',
    'pct',
    efficiencyRows(bundle, history),
  );
}
